import React, { FC, useEffect, useRef, useState } from 'react';
import { Animated, TouchableWithoutFeedback, View, ViewStyle, StyleProp } from 'react-native';
import ReactDOM from 'react-dom';
import { Portal } from 'react-native-portalize';
import { ModalWrapperProps } from './ModalWrapper';
import { fullWidth, scale as initialScale } from '@components/frame';
import { isWeb } from '@helpers';

export type LayeredModalAnimationType = 'fade' | 'slideFromRight';

export interface LayeredModalProps
  extends Pick<ModalWrapperProps, 'C' | 'backdropOpacity' | 'animationInTiming' | 'animationOutTiming' | 'scale' | 'children'> {
  isVisible: boolean;
  onBackdropPress: () => void;
  backdropColor?: string;
  contentStyle?: StyleProp<ViewStyle>;
  zIndex?: number;
  centered?: boolean;
  usePortal?: boolean;
  animationType?: LayeredModalAnimationType;
  portalToBody?: boolean;
}

const LayeredModal: FC<LayeredModalProps> = ({
  isVisible,
  onBackdropPress,
  C,
  children,
  backdropColor = 'Overlay/Light/Dim strong',
  backdropOpacity = 1,
  animationInTiming = 300,
  animationOutTiming = 300,
  scale = initialScale,
  contentStyle,
  zIndex = 9999,
  centered = true,
  usePortal = true,
  animationType = 'fade',
  portalToBody = false,
}) => {
  const isSlideFromRight = animationType === 'slideFromRight';
  const [isModalVisible, setIsModalVisible] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isVisible) {
      setIsModalVisible(true);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: animationInTiming,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: animationOutTiming,
        useNativeDriver: true,
      }).start(() => {
        setIsModalVisible(false);
      });
    }
  }, [isVisible, fadeAnim, animationInTiming, animationOutTiming]);

  if (!isModalVisible) {
    return null;
  }

  const modalContent = (
    <Animated.View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: backdropColor === 'transparent' ? backdropColor : C(backdropColor),
          justifyContent: centered ? 'center' : undefined,
          alignItems: centered ? 'center' : undefined,
          zIndex,
          opacity: isSlideFromRight
            ? backdropOpacity
            : fadeAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [0, backdropOpacity],
              }),
        }}
      >
        <TouchableWithoutFeedback onPress={onBackdropPress}>
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        </TouchableWithoutFeedback>
        <Animated.View
          style={[
            centered && {
              backgroundColor: C('Background/Light/Primary'),
              borderRadius: scale(12),
            },
            isSlideFromRight
              ? {
                  transform: [
                    {
                      translateX: fadeAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [fullWidth, 0],
                      }),
                    },
                  ],
                }
              : {
                  opacity: fadeAnim,
                  transform: centered
                    ? [
                        {
                          scale: fadeAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [0.9, 1],
                          }),
                        },
                      ]
                    : undefined,
                },
            contentStyle,
          ]}
        >
          {children}
        </Animated.View>
      </Animated.View>
  );

  if (portalToBody && isWeb && typeof document !== 'undefined') {
    return ReactDOM.createPortal(modalContent, document.body);
  }

  return usePortal ? <Portal>{modalContent}</Portal> : modalContent;
};

export default LayeredModal;
